import type { LucideIcon } from 'lucide-react'
import {
  BookUser,
  Building2,
  Container,
  FileText,
  LayoutDashboard,
  MessageSquare,
  Settings,
  Wallet,
} from 'lucide-react'

import type { Role, SeededRole } from '@/types/domain'

/* Navigation is filtered by role, so nobody is shown a section they cannot
 * enter. EXPERIENCE.md § Permission-Shaped UI: PREFER ABSENCE TO DISABLED
 * CONTROLS. A Sales user does not see a greyed-out Finance section; the section
 * is not in their navigation. Disabled controls invite the question "why not",
 * and at this team size the answer is a WhatsApp message to a founder.
 *
 * This is a UX affordance ONLY. Server-side authorisation is the sole real
 * enforcement (AD-21) — the client never treats hiding as security, and the API
 * returns no data the user may not see regardless of what is rendered here. */

/* Labels are i18n KEYS, not text. Navigation is module-level data evaluated
 * once at import, so a translated string baked in here would be frozen in
 * whichever language happened to be active at startup and would not follow a
 * language switch. The component translates at render instead. */

/** The `nav` catalogue keys usable as a navigation label. Typed as literals so
 *  `t(item.labelKey)` satisfies i18next's strict key checking. */
export type NavLabelKey =
  | 'nav.dashboard'
  | 'nav.quotes'
  | 'nav.cases'
  | 'nav.chat'
  | 'nav.finance'
  | 'nav.clients'
  | 'nav.directories'
  | 'nav.settings'

export type NavSectionKey = 'nav.sections.main' | 'nav.sections.company'

export interface NavItem {
  to: string
  /** Key into the `nav` catalogue, e.g. `nav.cases`. */
  labelKey: NavLabelKey
  icon: LucideIcon
  /** Roles that see this item. Omitted means every role. */
  roles?: Role[]
  /** Key into the counts map, when the item shows a count badge. */
  countKey?: 'quotes' | 'cases' | 'chat'
}

export interface NavSection {
  /** Key into the `nav.sections` catalogue. */
  labelKey: NavSectionKey
  items: NavItem[]
}

const SECTIONS: NavSection[] = [
  {
    labelKey: 'nav.sections.main',
    items: [
      { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard },
      { to: '/quotes', labelKey: 'nav.quotes', icon: FileText, countKey: 'quotes' },
      { to: '/cases', labelKey: 'nav.cases', icon: Container, countKey: 'cases' },
      // Chat spans quotes AND cases, so it sits beside them rather than under
      // either. Its badge is the reader's total unread across both tabs — the
      // one number that answers "is anybody waiting on me".
      { to: '/chat', labelKey: 'nav.chat', icon: MessageSquare, countKey: 'chat' },
      {
        to: '/finance',
        labelKey: 'nav.finance',
        icon: Wallet,
        // Cross-case money is Finance and Super-admin only. The section is
        // absent for everyone else, and so are the dashboard money tiles.
        roles: ['finance', 'super-admin'],
      },
    ],
  },
  {
    labelKey: 'nav.sections.company',
    items: [
      /* Clients is the directory tab people actually come for — every quote
       * starts by picking one — so it is a section of its own with its own
       * path, and a client has a page under it (`/clients/:clientId`). */
      { to: '/clients', labelKey: 'nav.clients', icon: Building2 },
      { to: '/directories', labelKey: 'nav.directories', icon: BookUser },
      {
        to: '/settings',
        labelKey: 'nav.settings',
        icon: Settings,
        roles: ['super-admin'],
      },
    ],
  },
]

export function navigationFor(role: Role): NavSection[] {
  return SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.roles || item.roles.includes(role)),
  })).filter((section) => section.items.length > 0)
}

/** Whether a nav item is the one the current location is on.
 *
 *  `NavLink`'s own `isActive` matches on PATHNAME only, which cannot separate
 *  two items that share a pathname and differ by a search parameter: both
 *  would light up, and the sidebar's sliding pill — one element with a shared
 *  layoutId — cannot be in two places, so it would flicker between them.
 *
 *  The rule is narrow on purpose. An item carrying a search param is active
 *  only when the location's parameters agree with the ones it names (extra
 *  parameters the item does not mention are ignored, so a `?client=` beside
 *  the one it claims does not deactivate it). An item carrying none is active
 *  on its path only when no SIBLING item claims the current parameters.
 *
 *  `siblings` is the other items sharing this item's pathname; the caller has
 *  the section list in hand and this stays a pure function of it. */
export function isNavItemActive(
  item: Pick<NavItem, 'to'>,
  location: { pathname: string; search: string },
  siblings: readonly Pick<NavItem, 'to'>[] = [],
): boolean {
  const [path, query = ''] = item.to.split('?')
  // The dashboard is the only prefix that would swallow every route below it;
  // every other item owns its path AND the records under it — a case's page
  // is still the Cases section, a client's still Clients.
  const pathMatches =
    path === '/'
      ? location.pathname === '/'
      : location.pathname === path || location.pathname.startsWith(path + '/')
  if (!pathMatches) return false

  const current = new URLSearchParams(location.search)
  const claims = (spec: string) =>
    [...new URLSearchParams(spec)].every(([k, v]) => current.get(k) === v)

  if (query) return claims(query)

  return !siblings.some((other) => {
    const [otherPath, otherQuery = ''] = other.to.split('?')
    return otherPath === path && otherQuery !== '' && claims(otherQuery)
  })
}

/** Whether a role may see cross-case money surfaces (Finance, money tiles). */
export function canSeeCrossCaseFinance(role: Role): boolean {
  return role === 'finance' || role === 'super-admin'
}

/* The home surface, by role.
 *
 * "/" is not the dashboard for everyone. The dashboard answers the founders'
 * morning question — six live figures across every case — which is exactly the
 * cross-case money view Sales, Pricing and Operations may not see. Landing them
 * on it would open their working day on a page of suppressed tiles, and landing
 * Pricing on a page whose first widget they cannot read teaches them the
 * application is not for them.
 *
 * So each role starts where their work is: the founders and Finance on the
 * dashboard, Sales on quotes, Pricing on the quotes assigned to them,
 * Operations on cases.
 *
 * The filters are search params rather than separate routes because they are
 * the same list with a different WHERE clause — and because the server scopes
 * rows regardless (AD-5), so this decides what a user is SHOWN first, never
 * what they may read. A Sales user removing `?assignee=me` sees their own rows
 * either way. */
export function homePathFor(role: Role): string {
  switch (role) {
    case 'super-admin':
    case 'finance':
      return '/'
    case 'sales':
      return '/quotes'
    case 'pricing':
      // Pricing works the cost side of quotes handed to them by Sales, so an
      // unfiltered pipeline is somebody else's list.
      return '/quotes?assignee=me'
    case 'operations':
      return '/cases?assignee=me'
    default:
      // A role created at runtime (migration 0021). There is no way to guess
      // where its work lives, so it lands on the case list — the surface every
      // seeded role except Sales starts on, and the one whose row scope is
      // narrowest by default. The server decides what is actually on it; an
      // empty list is a correct answer for a role granted no case scope, and a
      // far better one than a blank redirect target.
      return '/cases'
  }
}

/** Whether this role's home surface is the dashboard itself.
 *
 * Exists so the index route can render the dashboard in place rather than
 * redirecting to it — a redirect from "/" to "/" is a loop. */
export function homeIsDashboard(role: Role): boolean {
  return homePathFor(role) === '/'
}

/** The `roles` catalogue key for one of the five SEEDED roles.
 *
 *  Roles are runtime-definable since migration 0021, so a role key is no longer
 *  guaranteed to have a translation — a founder can create `ops-lead` and the
 *  catalogue knows nothing about it. This narrows to the seeded five, and
 *  `roleLabel` below is what call sites should use.
 *
 *  Translate at the point of render: `t(roleLabelKey('sales'))`. */
export function roleLabelKey(role: SeededRole): `roles.${SeededRole}` {
  return `roles.${role}`
}

/** The five keys the catalogue has translations for. */
const SEEDED_ROLES: readonly SeededRole[] = [
  'sales',
  'pricing',
  'operations',
  'finance',
  'super-admin',
]

/** Whether a role key is one of the five the product ships with. */
export function isSeededRole(role: Role): role is SeededRole {
  return (SEEDED_ROLES as readonly string[]).includes(role)
}

/** How to render a role's name.
 *
 *  The five shipped roles are TRANSLATED — "Pricing" reads as «Ценообразование»
 *  in Russian, and a founder switching languages should not see an English word
 *  in a Russian sentence. A role somebody created at runtime has no translation
 *  and never will, so it falls back to the display name they typed. That is
 *  precisely why the server stores a name alongside the key.
 *
 *  Returns a discriminated result rather than calling `t` itself, because this
 *  module is data and must not depend on an i18n instance — the caller has a
 *  `t` in hand and this tells it which of the two things to do. */
export function roleLabel(
  role: Role,
  name?: string,
): { translated: true; key: `roles.${SeededRole}` } | { translated: false; text: string } {
  if (isSeededRole(role)) {
    return { translated: true, key: roleLabelKey(role) }
  }
  // The typed name when we have one; otherwise the raw key, which is at least
  // something a founder recognises from the role editor.
  return { translated: false, text: name?.trim() || role }
}
